import React, { useEffect, useState } from 'react';
import { Flame, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMyLitCandles } from '../lib/api';
import PrayerCard from '../components/PrayerCard';

/**
 * "My Lit Candles" page — routed at /feed/liked. Lists every prayer the
 * current user has lit at least one candle on, most-recently-lit first.
 *
 * Layout intentionally mirrors YourPrayers > "Prayers" tab (same responsive
 * PrayerCard grid, same empty-state pattern, same skeleton container) so the
 * two pages feel like siblings rather than divergent one-offs.
 */
const EmptyState = ({ icon: Icon, title, subtitle, cta, onCta }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center px-6" data-testid="lit-candles-empty">
    <div className="w-16 h-16 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
      <Icon size={28} className="text-amber-500" />
    </div>
    <h3 className="text-lg font-medium mb-1">{title}</h3>
    <p className="text-sm text-zinc-500 max-w-md">{subtitle}</p>
    {cta && (
      <button
        onClick={onCta}
        data-testid="lit-candles-empty-cta"
        className="mt-5 h-9 px-4 rounded-full text-sm font-medium bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
      >
        {cta}
      </button>
    )}
  </div>
);

const LitCandles = () => {
  const { user, loading: authLoading, openAuthModal: login } = useAuth();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMyLitCandles()
      .then((data) => { if (!cancelled) setPrayers(Array.isArray(data) ? data : []); })
      .catch((e) => {
        console.error('lit candles fetch failed', e);
        if (!cancelled) setError('Could not load your lit candles.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  if (authLoading) {
    return <div className="px-6 py-20 text-center text-zinc-500">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="px-4 sm:px-6 py-20" data-testid="lit-candles-signed-out">
        <EmptyState
          icon={Flame}
          title="Sign in to see your lit candles"
          subtitle="Every candle you light for a prayer will be gathered here — most recent first — so you can return whenever you'd like to pray again."
          cta="Sign in"
          onCta={login}
        />
      </div>
    );
  }

  return (
    <div data-testid="lit-candles-page">
      {/* Header — same visual weight as YourPrayers so the two pages feel
          consistent when a user flips between them from the sidebar. */}
      <div className="px-4 sm:px-6 pt-6 pb-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center">
            <Flame size={22} className="text-amber-600" fill="rgba(251, 191, 36, 0.35)" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold" data-testid="lit-candles-title">My Lit Candles</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
              Prayers you&apos;ve lit a candle for, most recent first.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 py-6">
        {loading && (
          <div
            className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2"
            data-testid="lit-candles-loading"
          >
            <Loader2 className="animate-spin" size={16} /> Loading your lit candles…
          </div>
        )}
        {!loading && error && (
          <div className="py-16 text-center text-red-600 dark:text-red-400 text-sm" data-testid="lit-candles-error">
            {error}
          </div>
        )}
        {!loading && !error && prayers.length === 0 && (
          <div className="py-16 text-center" data-testid="lit-candles-empty">
            <p className="text-zinc-600 dark:text-zinc-400">
              You haven&apos;t lit any candles yet.
            </p>
          </div>
        )}
        {!loading && !error && prayers.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-x-4 gap-y-8"
            data-testid="lit-candles-grid"
          >
            {prayers.map((p) => <PrayerCard key={p.id} prayer={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default LitCandles;
